import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../../services/baseapi';

// ── Types ─────────────────────────────────────────────────────────────────────

export type WizardStep = 'upload' | 'configure' | 'preview' | 'export';
export type KeyFormat = 'RESP' | 'CODE' | 'ID' | 'CUSTOM';

export interface DetectedColumn {
  column: string;
  piiType: string;
  selected: boolean;
}

export interface KeyMapping {
  key: string;
  originalValue: string;
  type: string;
  column: string;
}

export interface AnonymisationJob {
  id: string;
  fileName: string;
  standardUsed: string;
  columnsAnonymised: number;
  valuesAnonymised: number;
  createdAt: string;
  keyAvailableUntil: string;
  keyMapping?: KeyMapping[];
  headers?: string[];
  anonymisedRows?: Record<string, string>[];
}

export interface LibraryStandardOption {
  id: string;
  name: string;
  description: string;
}

// ── Mock Library Standards ─────────────────────────────────────────────────────
// Replace with: GET /api/projects/:id/library?type=imported_standard

export const MOCK_LIBRARY_STANDARDS: LibraryStandardOption[] = [
  {
    id: 'who',
    name: 'WHO Health Indicators',
    description: 'Patient identifying info: name, address, date of birth, phone, email, national ID',
  },
  {
    id: 'sphere',
    name: 'Sphere Standards',
    description: 'Beneficiary info: name, address, phone number, household ID',
  },
  {
    id: 'sdg',
    name: 'SDGs — 17 Goals',
    description: 'Respondent info: name, address, phone, email, organisation name',
  },
  {
    id: 'oecd',
    name: 'OECD Development Stats',
    description: 'Survey respondent info: name, address, email, national ID',
  },
  {
    id: 'worldbank',
    name: 'World Bank Poverty Data',
    description: 'Household info: name, address, household ID, income data',
  },
];

// ── PII keyword definitions per standard ─────────────────────────────────────
// Each entry lists column-header substrings that identify a PII type.
// Matching is case-insensitive, ignoring spaces/underscores/hyphens.

const STANDARD_PII_FIELDS: Record<string, { keywords: string[]; piiType: string }[]> = {
  who: [
    { keywords: ['name', 'patient', 'fullname', 'firstname', 'lastname', 'givenname', 'surname'], piiType: 'Person Name' },
    { keywords: ['address', 'location', 'street', 'city', 'district', 'region', 'village', 'ward'], piiType: 'Address' },
    { keywords: ['dob', 'birth', 'birthdate', 'dateofbirth', 'age'], piiType: 'Date of Birth' },
    { keywords: ['phone', 'mobile', 'tel', 'contact', 'telephone', 'cellphone'], piiType: 'Phone Number' },
    { keywords: ['email', 'mail'], piiType: 'Email' },
    { keywords: ['nationalid', 'national', 'passport', 'patientid', 'caseid', 'pid', 'uid', 'ssn'], piiType: 'ID / Case Number' },
    { keywords: ['gps', 'latitude', 'longitude', 'lat', 'lon', 'coordinates'], piiType: 'GPS Coordinates' },
  ],
  sphere: [
    { keywords: ['name', 'beneficiary', 'fullname', 'firstname', 'lastname', 'head', 'member'], piiType: 'Person Name' },
    { keywords: ['address', 'location', 'camp', 'shelter', 'site', 'settlement', 'block', 'sector'], piiType: 'Address' },
    { keywords: ['phone', 'mobile', 'tel', 'contact'], piiType: 'Phone Number' },
    { keywords: ['hhid', 'householdid', 'household', 'beneficiaryid', 'caseid', 'id'], piiType: 'ID / Case Number' },
    { keywords: ['dob', 'birth', 'age', 'birthdate'], piiType: 'Date of Birth' },
    { keywords: ['gps', 'latitude', 'longitude', 'lat', 'lon'], piiType: 'GPS Coordinates' },
  ],
  sdg: [
    { keywords: ['name', 'respondent', 'fullname', 'firstname', 'lastname', 'interviewee'], piiType: 'Person Name' },
    { keywords: ['address', 'location', 'street', 'city', 'district', 'region'], piiType: 'Address' },
    { keywords: ['phone', 'mobile', 'tel', 'contact'], piiType: 'Phone Number' },
    { keywords: ['email', 'mail'], piiType: 'Email' },
    { keywords: ['org', 'organisation', 'organization', 'company', 'employer'], piiType: 'Organisation Name' },
    { keywords: ['id', 'respondentid', 'uid', 'nationalid'], piiType: 'ID' },
  ],
  oecd: [
    { keywords: ['name', 'fullname', 'firstname', 'lastname', 'respondent'], piiType: 'Person Name' },
    { keywords: ['address', 'location', 'street', 'city', 'postal'], piiType: 'Address' },
    { keywords: ['email', 'mail'], piiType: 'Email' },
    { keywords: ['nationalid', 'national', 'id', 'ssn', 'taxid'], piiType: 'National ID' },
    { keywords: ['phone', 'mobile', 'tel'], piiType: 'Phone Number' },
  ],
  worldbank: [
    { keywords: ['name', 'head', 'fullname', 'firstname', 'lastname', 'household'], piiType: 'Person Name' },
    { keywords: ['address', 'location', 'village', 'district', 'region', 'province'], piiType: 'Address' },
    { keywords: ['hhid', 'householdid', 'household', 'caseid', 'id'], piiType: 'Household ID' },
    { keywords: ['phone', 'mobile', 'tel', 'contact'], piiType: 'Phone Number' },
    { keywords: ['dob', 'birth', 'age'], piiType: 'Date of Birth' },
  ],
};

// ── Core utilities ─────────────────────────────────────────────────────────────

export function detectPiiColumns(headers: string[], standardId: string): DetectedColumn[] {
  const fields = STANDARD_PII_FIELDS[standardId] ?? [];
  return headers.map(header => {
    const normalised = header.toLowerCase().replace(/[\s_\-\.]/g, '');
    for (const field of fields) {
      const matched = field.keywords.some(kw =>
        normalised.includes(kw.replace(/[\s_\-\.]/g, '')),
      );
      if (matched) {
        return { column: header, piiType: field.piiType, selected: true };
      }
    }
    return { column: header, piiType: 'Non-PII', selected: false };
  });
}

export async function parseUploadedFile(
  file: File,
): Promise<{ headers: string[]; rows: Record<string, string>[] }> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', codepage: 65001 });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];

  if (raw.length === 0) throw new Error('The file appears to be empty.');

  const headers = (raw[0] as any[])
    .map((h: any) => String(h ?? '').trim())
    .filter(Boolean);

  if (headers.length === 0) throw new Error('No column headers found in the file.');

  const rows = raw.slice(1).map(row => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = row[i] != null ? String(row[i]) : '';
    });
    return obj;
  });

  return { headers, rows };
}

export function generateKeyMapping(
  rows: Record<string, string>[],
  columns: DetectedColumn[],
  prefix: string,
): KeyMapping[] {
  const selected = columns.filter(c => c.selected);
  // One key per unique value, shared across all selected columns
  const valueToKey = new Map<string, string>();
  const mapping: KeyMapping[] = [];
  let counter = 1;

  rows.forEach(row => {
    selected.forEach(col => {
      const val = (row[col.column] ?? '').trim();
      if (!val) return;
      if (!valueToKey.has(val)) {
        const padded = String(counter).padStart(counter > 999 ? 4 : 3, '0');
        const key = `${prefix}_${padded}`;
        valueToKey.set(val, key);
        mapping.push({ key, originalValue: val, type: col.piiType, column: col.column });
        counter++;
      }
    });
  });

  return mapping;
}

export function buildAnonymisedRows(
  rows: Record<string, string>[],
  mapping: KeyMapping[],
): Record<string, string>[] {
  const valueToKey = new Map(mapping.map(m => [m.originalValue, m.key]));
  const selectedColumns = new Set(mapping.map(m => m.column));

  return rows.map(row => {
    const newRow = { ...row };
    selectedColumns.forEach(col => {
      const val = (newRow[col] ?? '').trim();
      if (val && valueToKey.has(val)) {
        newRow[col] = valueToKey.get(val)!;
      }
    });
    return newRow;
  });
}

export function downloadKeyMappingCSV(mapping: KeyMapping[], originalFileName: string) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(
    mapping.map(m => ({
      Key: m.key,
      'Original Value': m.originalValue,
      Type: m.type,
      Column: m.column,
    })),
  );
  XLSX.utils.book_append_sheet(wb, ws, 'Key Mapping');
  const baseName = originalFileName.replace(/\.[^/.]+$/, '');
  XLSX.writeFile(wb, `${baseName}_key_mapping.csv`);
}

export function downloadAnonymisedCSV(
  headers: string[],
  anonymisedRows: Record<string, string>[],
  originalFileName: string,
) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(anonymisedRows, { header: headers });
  XLSX.utils.book_append_sheet(wb, ws, 'Anonymised');
  const baseName = originalFileName.replace(/\.[^/.]+$/, '');
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${baseName}_anonymised_${today}.csv`);
}

export function getKeyExpiryLabel(availableUntil: string): { expired: boolean; label: string } {
  const diff = dayjs(availableUntil).diff(dayjs(), 'day');
  if (diff < 0) return { expired: true, label: 'Key expired' };
  if (diff === 0) return { expired: false, label: 'Key expires today' };
  return { expired: false, label: `Key available for ${diff} more day${diff === 1 ? '' : 's'}` };
}

// ── In-memory blob builders ────────────────────────────────────────────────────

export function buildKeyMappingBlob(mapping: KeyMapping[]): Blob {
  const header = 'Key,Original Value,Type,Column';
  const rows = mapping.map(m =>
    [m.key, m.originalValue, m.type, m.column]
      .map(v => `"${String(v).replace(/"/g, '""')}"`)
      .join(','),
  );
  return new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
}

export function buildAnonymisedBlob(
  headers: string[],
  anonymisedRows: Record<string, string>[],
): Blob {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(anonymisedRows, { header: headers });
  XLSX.utils.book_append_sheet(wb, ws, 'Anonymised');
  const buf = XLSX.write(wb, { type: 'array', bookType: 'csv' });
  return new Blob([buf], { type: 'text/csv' });
}

// Maps a Library standard title to a PII keyword-set key
export function resolveKeyFromName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('who')) return 'who';
  if (lower.includes('sphere')) return 'sphere';
  if (lower.includes('sdg') || lower.includes('sustainable development')) return 'sdg';
  if (lower.includes('oecd')) return 'oecd';
  if (lower.includes('world bank')) return 'worldbank';
  return 'who';
}

// ── API calls ─────────────────────────────────────────────────────────────────
// Group 1 — Library standards (reuses existing library endpoint)

export async function fetchLibraryStandards(
  projectId: string,
): Promise<LibraryStandardOption[]> {
  const { data } = await api.get(`/library/projects/${projectId}/references`, {
    params: { type: 'imported_standard', limit: 100 },
  });
  const items: any[] = Array.isArray(data) ? data : (data.items ?? []);
  return items.map(item => ({
    id: item.reference_id ?? item.id,
    name: item.title,
    description: item.description ?? '',
  }));
}

// Group 4 — Save anonymised file to project
export async function saveAnonymisedFile(
  projectId: string,
  file: File,
  originalFileName: string,
): Promise<{ fileId: string; name: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('label', 'anonymised');
  form.append('original_file_name', originalFileName);
  const { data } = await api.post(`/projects/${projectId}/files`, form);
  return { fileId: data.file_id, name: data.name };
}

// Group 2+3 — Create job record + upload key mapping
export async function createAnonymisationJob(
  projectId: string,
  payload: {
    fileName: string;
    standardUsed: string;
    columnsAnonymised: number;
    valuesAnonymised: number;
    keyFormat: string;
    keyFile: File;
  },
): Promise<{ jobId: string; keyAvailableUntil: string }> {
  const form = new FormData();
  form.append('file_name', payload.fileName);
  form.append('standard_used', payload.standardUsed);
  form.append('columns_anonymised', String(payload.columnsAnonymised));
  form.append('values_anonymised', String(payload.valuesAnonymised));
  form.append('key_format', payload.keyFormat);
  form.append('key_file', payload.keyFile);
  const { data } = await api.post(`/projects/${projectId}/anonymisation/jobs`, form);
  return { jobId: data.job_id, keyAvailableUntil: data.key_available_until };
}

// Group 2 — List jobs (history)
export async function listAnonymisationJobs(
  projectId: string,
): Promise<AnonymisationJob[]> {
  const { data } = await api.get(`/projects/${projectId}/anonymisation/jobs`);
  return (data as any[]).map(j => ({
    id: j.job_id,
    fileName: j.file_name,
    standardUsed: j.standard_used,
    columnsAnonymised: j.columns_anonymised,
    valuesAnonymised: j.values_anonymised,
    createdAt: j.created_at,
    keyAvailableUntil: j.key_available_until,
  }));
}

// Group 3 — Re-export key CSV (admin only, 30-day window)
export async function reexportKeyCSV(
  projectId: string,
  jobId: string,
  fileName: string,
): Promise<void> {
  const { data } = await api.get(
    `/projects/${projectId}/anonymisation/jobs/${jobId}/key`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName.replace(/\.[^/.]+$/, '')}_key_mapping.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
